/**
 * ConfigManager - Менеджер для управління конфігураціями застосунку
 *
 * Надає централізоване управління налаштуваннями застосунку
 * з типізацією та реактивністю через MobX
 */

import { action, makeObservable } from 'mobx'
import { loggerManager } from '../Logger'

export interface ConfigValue {
    [key: string]: unknown
}

export interface ConfigManagerOptions {
    debug?: boolean
    context?: Record<string, unknown>
}

export class ConfigManager {
    private _store = new Map<string, unknown>()
    private debug: boolean
    private context: Record<string, unknown>

    constructor(options: ConfigManagerOptions = {}) {
        this.debug = options.debug ?? false
        this.context = options.context ?? {}

        makeObservable(this, {
            sets: action,
            set: action,
            clear: action,
            delete: action,
        })
    }

    /**
     * Встановлює множинні конфігурації з об'єкта
     */
    sets(configs: Record<string, unknown>): void {
        if (typeof configs !== 'object' || configs === null) {
            if (this.debug) {
                loggerManager.warn('[ConfigManager] sets: Передані некоректні дані', 'config-manager', { configs })
            }
            return
        }

        for (const key in configs) {
            if (configs.hasOwnProperty(key)) {
                this._store.set(key, configs[key])
            }
        }
    }

    /**
     * Встановлює одну конфігурацію
     */
    set(key: string, value: unknown): void {
        this._store.set(key, value)
    }

    /**
     * Отримує конфігурацію по ключу
     */
    get<T = unknown>(key: string, defaultValue?: T): T | undefined {
        const value = this._store.get(key) as T | undefined
        return value !== undefined ? value : defaultValue
    }

    /**
     * Отримує всі конфігурації у вигляді об'єкта
     */
    getAll(): Record<string, unknown> {
        const result: Record<string, unknown> = {}
        this._store.forEach((value: unknown, key: string) => {
            result[key] = value
        })
        return result
    }

    /**
     * Перевіряє наявність конфігурації
     */
    has(key: string): boolean {
        return this._store.has(key)
    }

    /**
     * Видаляє конфігурацію по ключу
     */
    delete(key: string): boolean {
        const result = this._store.delete(key)

        return result
    }

    /**
     * Очищає всі конфігурації
     */
    clear(): void {
        this._store.clear()
    }

    /**
     * Отримує кількість конфігурацій
     */
    get size(): number {
        return this._store.size
    }

    /**
     * Отримує всі ключі конфігурацій
     */
    get keys(): string[] {
        return Array.from(this._store.keys())
    }

    /**
     * Отримує всі значення конфігурацій
     */
    get values(): any[] {
        return Array.from(this._store.values())
    }

    /**
     * Отримує всі записи конфігурацій
     */
    get entries(): [string, any][] {
        return Array.from(this._store.entries())
    }

    /**
     * Перевіряє, чи порожній стор
     */
    get isEmpty(): boolean {
        return this._store.size === 0
    }

    /**
     * Повертає JSON представлення всіх конфігурацій
     */
    toJSON(): Record<string, any> {
        return this.getAll()
    }

    /**
     * Виводить інформацію про стан менеджера
     */
    debug_info(): void {
        console.group('[ConfigManager] Debug Info')
        console.log('Розмір:', this.size)
        console.log('Контекст:', this.context)
        console.log('Конфігурації:', this.getAll())
        console.groupEnd()
    }
}

/**
 * Фабрична функція для створення екземпляра ConfigManager
 */
export function createConfigManager(options?: ConfigManagerOptions): ConfigManager {
    return new ConfigManager(options)
}

/**
 * Глобальний екземпляр ConfigManager для загального використання
 */
export const configManager = createConfigManager({
    debug: true, // Включаємо дебаг у розробці
})

export default ConfigManager
